import React, { useEffect, useState } from 'react';
import { X, FileText, Receipt as ReceiptIcon } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  invoice:       { label: 'Invoice',       icon: FileText,    goto: (r) => `/admin/invoices/${r.ref_id}` },
  quote_payment: { label: 'Quote payment', icon: FileText,    goto: (r) => `/admin/quotes/${r.ref_id}` },
  receipt:       { label: 'Receipt',       icon: ReceiptIcon, goto: (r) => `/admin/receipts/${r.ref_id}` },
};

/**
 * Drill-down modal for YTD / All-time revenue.
 * Fetches `/analytics/revenue-rows?window=…` which returns rows grouped by
 * month (YTD) or by year (all-time). Rows deep-link to their source doc.
 */
export const RevenueDetailModal = ({ window: revWindow, title, onClose, navigate }) => {
  const password = sessionStorage.getItem('admin_password');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/analytics/revenue-rows?window=${revWindow}`, {
          headers: { Authorization: `Basic ${btoa(':' + password)}` },
        });
        if (!res.ok) throw new Error('load failed');
        const d = await res.json();
        if (!cancelled) setData(d);
      } catch (err) {
        logger.error('Revenue rows:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [revWindow, password]);

  const goto = (row) => {
    const meta = TYPE_META[row.type];
    if (!meta || !row.ref_id) return;
    onClose();
    navigate(meta.goto(row));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`revenue-modal-${revWindow}`}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">{title || 'Revenue'}</h2>
            <p className="text-sm text-foreground/60">
              {loading ? 'Loading…' : `${data?.label || ''} · ${data?.count || 0} payment${data?.count === 1 ? '' : 's'} · CI$${Number(data?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close"
            data-testid="revenue-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-foreground/60">Loading…</div>
          ) : !data ? (
            <div className="p-10 text-center text-red-600">Failed to load</div>
          ) : data.groups.length === 0 ? (
            <div className="p-10 text-center text-foreground/50">No payments in this range.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.groups.map((g) => (
                <div key={g.key} data-testid={`revenue-group-${g.key}`}>
                  <div className="px-5 py-2 bg-gray-50 flex items-center justify-between sticky top-0">
                    <span className="font-semibold text-sm">{g.label}</span>
                    <span className="text-xs text-foreground/60">
                      {g.rows.length} · CI${Number(g.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {g.rows.map((r, idx) => {
                        const meta = TYPE_META[r.type];
                        const Icon = meta ? meta.icon : FileText;
                        const clickable = !!(meta && r.ref_id);
                        return (
                          <tr
                            key={`${r.type}-${r.ref_id || idx}`}
                            onClick={clickable ? () => goto(r) : undefined}
                            data-testid={`revenue-row-${r.type}-${r.ref_id || idx}`}
                            className={clickable ? 'hover:bg-gray-50 cursor-pointer' : ''}
                          >
                            <td className="px-5 py-2.5 text-foreground/70 font-mono text-xs w-24">{r.date}</td>
                            <td className="px-5 py-2.5 w-32">
                              <span className="inline-flex items-center gap-1.5 text-xs text-foreground/70">
                                <Icon className="w-3.5 h-3.5" />
                                {meta ? meta.label : r.type}
                              </span>
                            </td>
                            <td className="px-5 py-2.5 font-mono text-xs text-foreground/80 w-28">{r.ref || '—'}</td>
                            <td className="px-5 py-2.5 truncate max-w-[220px]">{r.client || '—'}</td>
                            <td className="px-5 py-2.5 text-right font-semibold">
                              {r.currency || 'CI$'}{Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueDetailModal;
